import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiSuccess, MyRegistration, ReuploadDocumentResult } from '@alamo/types';

import { apiClient } from '../client';
import { queryKeys } from '../query-keys';
import { DOCUMENT_UPLOAD_TIMEOUT_MS } from '../upload-timeouts';

/**
 * Candidate-authenticated view of the current user's registration (Phase 02.3).
 * GET /api/v1/profile/my-registration
 */
export function useMyRegistration(options?: {
  enabled?: boolean;
  refetchInterval?: number | false;
}) {
  return useQuery({
    queryKey: queryKeys.profile.myRegistration(),
    queryFn: async () => {
      const response = await apiClient.get<ApiSuccess<MyRegistration>>('/profile/my-registration');
      return response.data.data;
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: options?.refetchInterval ?? false,
    enabled: options?.enabled ?? true,
  });
}

export interface ReuploadPayload {
  documentId: string;
  file: File;
}

/**
 * Replace a rejected registration document.
 * PATCH /api/v1/profile/my-registration/documents/:id (multipart).
 */
export function useReuploadDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ documentId, file }: ReuploadPayload) => {
      const formData = new FormData();
      formData.append('file', file);
      // apiClient has `Content-Type: application/json` as a default header. For
      // multipart uploads we MUST null it out so the browser appends the
      // `boundary=...` parameter — otherwise axios keeps the default and
      // serializes the FormData as `{}`, so the backend receives no file.
      const response = await apiClient.patch<ApiSuccess<ReuploadDocumentResult>>(
        `/profile/my-registration/documents/${documentId}`,
        formData,
        {
          headers: { 'Content-Type': undefined },
          timeout: DOCUMENT_UPLOAD_TIMEOUT_MS,
        },
      );
      return response.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.profile.myRegistration() });
    },
  });
}
